import { type MutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { getGetDebtorQueryKey } from 'api';
import type { AxiosInstance } from 'axios';
import { type ApiError, type ApiResponse, useLegacyAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

export type PauseDebtorVariables = {
  id: number | string;
  data: {
    pause_debtor: boolean;
    reason_id?: string;
    end_at?: string;
  };
};

async function pauseDebtorFn(
  axiosInstance: Promise<AxiosInstance>,
  { id, data }: PauseDebtorVariables,
): Promise<ApiResponse> {
  const instance = await axiosInstance;

  return instance.post(`debtors/${id}/pause_debtor`, data);
}

export function usePauseDebtor(
  options: MutationOptions<ApiResponse, ApiError, PauseDebtorVariables> = {},
) {
  const client = useQueryClient();
  const axiosInstance = useLegacyAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, PauseDebtorVariables>({
    mutationFn: variables => pauseDebtorFn(axiosInstance, variables),
    onSuccess: (_data, { id }) => {
      client.invalidateQueries({ queryKey: getGetDebtorQueryKey(id) });
    },
    ...options,
  });

  return addActionNameToMutationResult<ApiResponse, ApiError, PauseDebtorVariables, 'pauseDebtor'>(
    'pauseDebtor',
    mutation,
  );
}
